import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { AnthropicAuthInfo } from "@/types";

// #227: Anthropic-Auth setzen/löschen (admin-only). Ohne agentId → Plattform-Default,
// mit agentId → per-Agent-Override. Invalidiert den passenden Query-Key.

function useClient() {
  const { session, logout } = useAuth();
  return () =>
    createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
}

function keyFor(agentId?: string) {
  return agentId
    ? queryKeys.subResource("agents", agentId, "anthropic-auth")
    : queryKeys.list("anthropic-auth");
}

function pathFor(agentId?: string) {
  return agentId ? `/agents/${agentId}/anthropic-auth` : "/anthropic-auth";
}

export interface SetAnthropicAuthInput {
  method: string;
  secret: string;
}

export function useSetAnthropicAuth(
  agentId?: string,
): UseMutationResult<AnthropicAuthInfo, Error, SetAnthropicAuthInput> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (input) => getClient().put<AnthropicAuthInfo>(pathFor(agentId), input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keyFor(agentId) }),
  });
}

export function useClearAnthropicAuth(
  agentId?: string,
): UseMutationResult<AnthropicAuthInfo, Error, void> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: () => getClient().delete<AnthropicAuthInfo>(pathFor(agentId)),
    onSuccess: () => qc.invalidateQueries({ queryKey: keyFor(agentId) }),
  });
}
